<template>
    <div id="report-input-header">
        <b-row class="justify-content-between">

            <div class="col-md-4">
                <div
                    id="popover-report-header"
                    class="report-input-overall-results"
                >
                    <h5>
                        {{ $t("report") }} {{ report.report_id }} ({{
                            parseFloat(stats.successRate).toFixed(2)
                        }}% {{ $t("pass") }})
                    </h5>
                    <b-popover
                        target="popover-report-header"
                        triggers="hover"
                        placement="top"
                    >
                        <template v-slot:title
                            >{{ $t("report") }} {{ report.report_id }}
                        </template>
                        <ul class="list-unstyled mb-0">
                            <li>
                                <strong>{{ $t("date") }}</strong
                                >: {{ report.report_date }}
                            </li>
                            <li>
                                <strong>{{ $t("manufacturer") }}</strong
                                >: {{ report.manufacturer.name }}
                            </li>
                            <li>
                                <strong>{{ $t("client") }}</strong
                                >:
                                <a
                                    :href="`/reports?filters%5Bsearch%5D=&filters%5Bclient%5D=${report.template.client_id}`"
                                    >{{ report.template.client.name }}</a
                                >
                            </li>
                            <li>
                                <strong>{{ $t("pass") }}</strong
                                >: {{ stats.pass }}
                            </li>
                            <li>
                                <strong>{{ $t("fail") }}</strong
                                >: {{ stats.fail }}
                            </li>
                            <li>
                                <strong>{{ $t("comment") }}</strong
                                >: {{ report.comment }}
                            </li>
                        </ul>
                    </b-popover>

                    <b-progress
                        :value="stats.successRate"
                        :max="100"
                        :precision="2"
                        :variant="getColorVariant(stats.successRate)"
                        show-progress
                    >
                    </b-progress>
                </div>
            </div>

            <div
                class="col-md-4 text-sm-center"
                id="report-input-saving-indicator"
            >
                <hr class="d-sm-none" />
                <h5 v-if="!savingRow && !queue" class="text-success">
                    <span v-if="lastSaveFromNow"
                        >{{ $t("last_saved") }}: {{ lastSaveFromNow }}</span
                    >
                    <span v-if="!online" class="alert-danger">{{
                        $t("Offline")
                    }}</span>
                </h5>
                <h5 v-else class="text-info">
                    {{ $t("report_saving") }} (Changes remaining: {{ queue }})
                </h5>
                <hr class="d-sm-none" />
                <div class="d-flex justify-content-center align-items-center">
                    <span class="d-none d-sm-block text-muted">{{
                        lastSaveDate.format("DD/MM/YYYY HH:mm:ss")
                    }}</span>
                    <b-button
                        class="ml-2"
                        @click="$emit('clearFilters')"
                        size="sm"
                        variant="outline-secondary"
                        v-if="activeFilters"
                        >{{ $t("clear_filters") }}
                    </b-button>
                </div>
            </div>

            <div class="col-md-4 text-right">

                    <reports-send-button
                        @success="showSuccess"
                        @error="showError"
                        :report-name="report.report_id"
                        :report-id="report.id"
                        :client-name="report.template.client.name"
                        :is-sent="report.date_sent !== null"
                        :disabled="savingRow || queue > 0"
                        :is-new="isNew"
                    ></reports-send-button>

                    <b-button
                        variant="outline-primary"
                        size="md"
                        v-b-modal.modal-edit-form
                    >
                        <font-awesome-icon :icon="['fas', 'pen']" />
                        {{ $t("edit") }}
                    </b-button>

                <reports-purchase-order-sync
                    :is-new="isNew"
                    :report="report"
                    @synchronized="$emit('synchronized', $event.target)"
                    :disabled="queue > 0 || !online"
                />

                <b-modal
                    id="modal-edit-form"
                    hide-footer
                    :title="$t('edit_report_information')"
                >
                    <report-edit-form :report-data="report" />
                </b-modal>

                <b-modal
                    id="modal-error"
                    size="sm"
                    title="Something went wrong :("
                    hide-footer
                >
                    <template>
                        <div class="d-flex justify-content-between flex-column">
                            <h6>{{ errorMessage }}</h6>
                            <b-button
                                v-if="showUnlockButton"
                                @click="unlockPassports"
                            >
                                Unlock Passports
                            </b-button>
                        </div>
                    </template>
                </b-modal>

                <b-modal
                    id="modal-success"
                    size="sm"
                    title="Success"
                    hide-footer
                >
                    <template>
                        <div class="d-flex justify-content-between flex-column">
                            <h6>{{ successMessage }}</h6>
                        </div>
                    </template>
                </b-modal>
            </div>
        </b-row>
    </div>
</template>

<script>
import moment from "moment";
import _ from "lodash";
import { getColorVariant } from "../../plugins/utilities";
import ReportEditForm from "../../components/Reports/EditForm";

export default {
  name: "ReportInputHeader",
  components: {
    ReportEditForm
  },
  props: {
    report: {
      type: Object,
      default: null
    },
    lastSaveDate: {
      type: Object,
      default: null
    },
    savingRow: {
      type: Boolean,
      default: false
    },
    queue: {
      type: Number,
      default: 0
    },
    online: {
      type: Boolean,
      default: true
    },
    isNew: {
      type: Boolean,
      default: true
    },
    activeFilters: {
      type: Number,
      default: 0
    },
    stats: {
      type: Object,
      default: () => ({
        pass: 0,
        fail: 0,
        successRate: 0
      })
    }
  },
  data: function() {
    return {
      errorMessage: "",
      successMessage: "",
      lastSaveDelta: moment(),
      clientViewUrl: `/reports/${this.report.template.client.slug}/${
        this.report.slug
      }/show`,
      showUnlockButton: false
    };
  },
  computed: {
    lastSaveFromNow() {
      // lastSaveDelta included here to enforce this computed property to rerender
      return this.lastSaveDelta && this.lastSaveDate instanceof moment
        ? this.lastSaveDate.fromNow()
        : null;
    }
  },
  mounted() {
    // Update last save delta to enforce rerender of computed property
    setInterval(() => (this.lastSaveDelta = moment()), 5000);
  },
  methods: {
    getColorVariant: getColorVariant,
    showSuccess(data) {
      this.successMessage = data.message;
      this.$bvModal.show("modal-success");

      setInterval(() => window.location.replace("/reports"), 3000);
    },
    showError(data) {
      this.errorMessage = data.message;

      if (data.errorCode == 1) {
        this.showUnlockButton = true;
      } else {
        this.showUnlockButton = false;
      }

      this.$bvModal.show("modal-error");
    },
    unlockPassports() {
      window.open("https://www.ionaute.com/", "_blank");
    }
  }
};
</script>
